import { Box, Stack, Typography, Grid, CircularProgress, Link } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import {
    Loading,
    Card,
    useQueryParams,
    Button,
    date,
    Alert,
    SimpleMarkdownRenderer,
    isBuyerPortalFeatureEnabled,
    useBuyerPortalSettings,
} from 'common';
import { useCompanyPublicData, useGetSupplierCompanyData, usePrefetchCompanyUserProfile } from '../../../services';
import { SubmissionMethodSelector } from './SubmissionMethodSelector';
import { FinishDocumentsDialog } from './FinishDocuments';
import { useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CompanyLogo, ContactInformation, PageWithCustomLefNav } from '../../../components';
import { routes } from '../../../signup-portal.routes';

function useLandingPageQueryParams() {
    const params = useQueryParams<{ regCode?: string }>();

    return useMemo(() => {
        const regCode = params.regCode;

        const isValidRegCode = typeof regCode === 'string' && regCode.length;

        return {
            regCode: isValidRegCode ? regCode : null,
        };
    }, [params]);
}

function CloudCaptureRedirectButton({
    buyerCompanyToken,
    buyerCompanyId,
}: {
    buyerCompanyToken: string;
    buyerCompanyId: number;
}) {
    const { regCode } = useLandingPageQueryParams();

    const {
        data: supplierCompanyData,
        error,
        isLoading,
    } = useGetSupplierCompanyData({
        registrationCode: regCode ?? undefined,
        search: buyerCompanyToken,
    });
    const prefetchCompanyUserProfile = usePrefetchCompanyUserProfile();

    const supplierCompanyId = supplierCompanyData?.ID ?? null;

    const isRegistrationCodeExpired = useMemo(() => {
        const registrationCodeExpiryDate = supplierCompanyData?.RegistrationCodeExpiryDate;
        if (!registrationCodeExpiryDate) {
            return true; // if the date is not available, consider it expired
        }

        try {
            const registrationCodeExpiryDateParsed = new Date(
                date.tzNormalization.normalizeDateTimeString(String(registrationCodeExpiryDate))
            ); // normalize the source date string and assume it's in PST if timezone information is not available

            return registrationCodeExpiryDateParsed < new Date();
        } catch (e) {
            console.error('Error parsing registration code expiry date', registrationCodeExpiryDate, e);
            return true; // if the date is not valid, consider it expired
        }
    }, [supplierCompanyData]);

    const supplierCompanyNotFound = supplierCompanyData === null;

    const history = useHistory();

    const redirectToSignUpForm = () => {
        if (!regCode) {
            return;
        }

        const searchParams = new URLSearchParams({ regCode });

        history.push(
            `${routes.supplierSignUpForm.path}`.replace(':companyToken', buyerCompanyToken).concat(`?${searchParams}`),
            {
                supplierCompanyId,
                buyerCompanyId,
            }
        );
    };

    const [isPrefetching, setIsPrefetching] = useState(false);

    if (isLoading) {
        return (
            <Box>
                <Loading />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert testId="error-loading-supplier-company-data" severity="error">
                Error loading company data, please try again.
            </Alert>
        );
    }

    if (supplierCompanyNotFound) {
        return (
            <Alert testId="supplier-company-not-found" severity="error">
                Invalid Invitation. Please contact Transcepta Support.
            </Alert>
        );
    }

    if (isRegistrationCodeExpired) {
        return (
            <Alert testId="supplier-company-reg-code-expired" severity="error">
                Invitation has expired. Please contact Transcepta Support.
            </Alert>
        );
    }

    return (
        <Stack direction="row" justifyContent="center" spacing={2}>
            <Button
                onClick={async () => {
                    setIsPrefetching(true);
                    await prefetchCompanyUserProfile({
                        buyerCompanyId,
                        supplierCompanyId: supplierCompanyId!,
                    });
                    setIsPrefetching(false);
                    redirectToSignUpForm();
                }}
                disabled={isLoading || isPrefetching || !supplierCompanyId}
                startIcon={(isLoading || isPrefetching) && <CircularProgress size="1rem" />}
            >
                Next
            </Button>
        </Stack>
    );
}

function CustomerMessageMarkdown({ markdown }: { markdown: string | null }) {
    if (!markdown) {
        return null;
    }

    return <SimpleMarkdownRenderer markdown={markdown} sx={{ textAlign: 'center' }} />;
}

function CustomerMessage({ message, registrationVideoSrc }: { message: string; registrationVideoSrc: string | null }) {
    const showVideo = registrationVideoSrc != null;

    if (!message && !showVideo) {
        return null;
    }

    return (
        <Box>
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                {message && (
                    <Grid item xs={12} md={showVideo ? 7 : 12} lg={showVideo ? 8 : 12}>
                        <Card
                            rootProps={{
                                sx: {
                                    boxShadow: 1,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                },
                            }}
                            contentProps={{
                                sx: {
                                    p: { xs: 3, md: 4 },
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                },
                            }}
                            content={<CustomerMessageMarkdown markdown={message} />}
                        />
                    </Grid>
                )}
                {showVideo && (
                    <Grid item xs={12} md={message ? 5 : 12} lg={message ? 4 : 12}>
                        <Card
                            rootProps={{
                                sx: {
                                    position: 'relative',
                                    overflow: 'hidden',
                                    width: '100%',
                                    paddingTop: '56.25%',
                                    boxShadow: 1,
                                },
                                'data-testid': 'customer-video-card',
                            }}
                            mediaProps={{
                                component: 'iframe',
                                src: registrationVideoSrc,
                                sx: {
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    width: '100%',
                                    height: '100%',
                                },
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

function TransceptaMessages({ messages }: { messages: string[] }) {
    if (!messages.length) {
        return null;
    }

    return (
        <Box>
            <Typography variant="h5" component="h2" textAlign="center" mb={3}>
                Connecting with the Transcepta network will allow you to:
            </Typography>
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                {messages.map((message, idx) => {
                    return (
                        <Grid
                            key={message}
                            item
                            xs={12}
                            md={12 / messages.length}
                            data-testid={`transcepta-message-${idx + 1}`}
                        >
                            <Card
                                rootProps={{
                                    sx: {
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: 1,
                                    },
                                }}
                                contentProps={{
                                    sx: {
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        p: 3,
                                    },
                                }}
                                content={
                                    <>
                                        <CheckCircleOutlineIcon
                                            color="primary"
                                            sx={{ fontSize: 48, mb: 2, opacity: 0.8 }}
                                        />
                                        <Typography variant="body1" component="p" sx={{ textAlign: 'center' }}>
                                            {message}
                                        </Typography>
                                    </>
                                }
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}

export function LandingPage() {
    const { companyToken } = useParams<{
        companyToken?: string;
    }>();

    const { regCode } = useLandingPageQueryParams();

    const history = useHistory();

    const {
        data: company,
        isLoading: isLoadingCompanyData,
        error,
    } = useCompanyPublicData({
        search: companyToken,
    });

    const { data: buyerPortalSettings, isInitialLoading: isLoadingBuyerPortalSettings } = useBuyerPortalSettings({
        companyId: company?.ID ?? null,
    });
    const showFinishDocuments =
        regCode == null && isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnableSupplierPostSignup');
    const [isFinishDocumentsOpen, setIsFinishDocumentsOpen] = useState(false);

    const handleOpenFinishDocuments = () => {
        setIsFinishDocumentsOpen(true);
    };

    const handleCloseFinishDocuments = () => {
        setIsFinishDocumentsOpen(false);
    };

    useEffect(() => {
        if (company !== null && error === null) {
            return;
        }

        /**
         * This will redirect the user to the Transcepta website.
         */
        history.replace('/');
    }, [company, history, error]);

    if (isLoadingCompanyData || (regCode == null && isLoadingBuyerPortalSettings)) {
        return <Loading />;
    }

    if (company == null) {
        return null;
    }

    const companyName = company?.Name ?? '';

    const title = (
        <Box textAlign="center" mb={{ xs: 1, md: 2 }}>
            <Typography variant="h2" component="h1" fontWeight={700} gutterBottom>
                {companyName}
            </Typography>
            <Typography variant="h5" fontWeight={400}>
                Connect to start sending documents electronically
            </Typography>
            {showFinishDocuments && (
                <Typography mt={2}>
                    Already registered?{' '}
                    <Link
                        component="button"
                        variant="body1"
                        onClick={handleOpenFinishDocuments}
                        sx={{ verticalAlign: 'baseline' }}
                    >
                        Finish your documents.
                    </Link>
                </Typography>
            )}
        </Box>
    );

    const contactInformation = (
        <ContactInformation
            companyName={companyName}
            links={{
                InvitationLink: regCode == null ? company?.InvitationLink ?? null : null,
                RegistrationContact: company.RegistrationContact,
            }}
        />
    );

    return (
        <PageWithCustomLefNav
            leftNavContent={
                <Stack gap={4} width="100%">
                    <CompanyLogo src={company.Logo} containerSx={{ boxShadow: 'none' }} />
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>{contactInformation}</Box>
                </Stack>
            }
        >
            <Stack
                spacing={{ xs: 3, md: 4, lg: 5 }}
                sx={{
                    width: '100%',
                    maxWidth: (t) => t.breakpoints.values.lg,
                    pb: { xs: 8, md: 12, lg: 16 },
                }}
            >
                {title}
                <CustomerMessage
                    message={company.RegistrationMessage ?? ''}
                    registrationVideoSrc={company.RegistrationVideo}
                />
                <TransceptaMessages
                    messages={
                        [
                            company.RegistrationMessage1,
                            company.RegistrationMessage2,
                            company.RegistrationMessage3,
                        ].filter(Boolean) as string[]
                    }
                />
                {regCode == null ? (
                    <SubmissionMethodSelector
                        companyToken={companyToken!}
                        companyId={company.ID}
                        companyName={companyName}
                        threshold={company.ManualInvoiceThreshold ?? 0}
                    />
                ) : (
                    <CloudCaptureRedirectButton buyerCompanyToken={companyToken!} buyerCompanyId={company.ID} />
                )}
                <Box sx={{ display: { lg: 'none' } }}>{contactInformation}</Box>
            </Stack>
            {showFinishDocuments && (
                <FinishDocumentsDialog
                    open={isFinishDocumentsOpen}
                    onClose={handleCloseFinishDocuments}
                    companyToken={companyToken!}
                    buyerCompanyId={company.ID}
                    buyerName={companyName}
                />
            )}
        </PageWithCustomLefNav>
    );
}
